import React, {useState, useEffect, forwardRef, useImperativeHandle} from "react";
import { Col, Row, } from "react-bootstrap";
import IFormWithConfig from "../../../models/misconfig/FormWithConfig";
import IArborProviderConfig from "../../../models/misconfig/arbor/ArborProviderConfig";
import BiometricFilterEdit from "../biometric-filter-edit";
import IBiometricsFilter from "../../../models/misconfig/BiometricsFilter";
import { GUID } from "../../../app/common/helper/Guid";
import { useArborApplications } from "../../../hooks/useArborApplications";
import { canonicalizeArborProviderConfig } from "../../../models/misconfig/arbor/canonicalizeArborProviderConfig";

interface IArborProviderConfigEditProps extends IFormWithConfig {
    arborApplicationID: GUID | null;
    setArborApplicationId: (applicationId: GUID | null) => void;
}

const ArborProviderConfigEdit = forwardRef((props: IArborProviderConfigEditProps, ref) => {
    const { configJson, setJsonString } = props;
    const [ArborConfig, setArborConfig] = useState<IArborProviderConfig | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const { data: applications = [], isLoading: isApplicationsLoading, isError: isApplicationsError } = useArborApplications();

    useEffect(() => {
        if (configJson) {
            const configObj: IArborProviderConfig = JSON.parse(configJson);
            const sanitizedConfig: IArborProviderConfig = canonicalizeArborProviderConfig(configObj);
            const sanitizedJson: string = JSON.stringify(sanitizedConfig);
            setArborConfig(sanitizedConfig);
            if (sanitizedJson !== configJson) {
                setJsonString(sanitizedJson);
            }
        } else {
            setArborConfig({
                ApiCredentials: {
                    EstablishmentUrl: ''
                },
                GraphQLPageSize: 500,
                GraphQLPhotoPageSize: 25,
                RetrieveAccountIDs: true,
                RetrieveTransitionalFSM: false,
                BiometricConsents: {
                    FaceRecognition: [],
                    Fingerprints: []
                }
            });
        }
    }, [configJson, setJsonString]);

    useImperativeHandle(ref, () => ({
        validate: function() : boolean {
            const newErrors: { [key: string]: string } = {};
            if(!ArborConfig?.ApiCredentials?.EstablishmentUrl || ArborConfig.ApiCredentials.EstablishmentUrl.trim() === '') {
                newErrors['EstablishmentUrl'] = 'Establishment URL is required';
            }
            if(!props.arborApplicationID || !applications.some((application) => application.applicationID === props.arborApplicationID)) {
                newErrors['ArborApplication'] = isApplicationsError ? 'Arbor applications could not be loaded' : 'A valid Arbor application is required';
            }
            if(ArborConfig?.GraphQLPageSize === undefined || ArborConfig.GraphQLPageSize <= 0) {
                newErrors['GraphQLPageSize'] = 'GraphQL Page Size must be a positive number';
            }
            if(ArborConfig?.GraphQLPhotoPageSize === undefined || ArborConfig.GraphQLPhotoPageSize <= 0) {
                newErrors['GraphQLPhotoPageSize'] = 'GraphQL Photo Page Size must be a positive number';
            }
            setErrors(newErrors);
            return Object.keys(newErrors).length === 0;
        }
    }));

    const handleChange = (key: string, value: any) => {
        if(ArborConfig === null) {
            return;
        }
        const updatedConfig = { ...ArborConfig, [key]: value };
        setArborConfig(updatedConfig);
        props.setJsonString(JSON.stringify(updatedConfig));
    }

    const handleApiCredentialsChange = (field: string, value: string) => {
        if(ArborConfig === null) {
            return;
        }
        const updatedConfig = { ...ArborConfig, ApiCredentials: { ...ArborConfig.ApiCredentials, [field]: value } };
        setArborConfig(updatedConfig);
        props.setJsonString(JSON.stringify(updatedConfig));
    };

    const handleBiometricFilterChange = (filter: IBiometricsFilter) => {
        if(ArborConfig === null) {
            return;
        }
        const updatedConfig = { ...ArborConfig, BiometricConsents: filter };
        setArborConfig(updatedConfig);
        props.setJsonString(JSON.stringify(updatedConfig));
    };

    const hasApplicationFeedback: boolean = Boolean(errors.ArborApplication) || (applications.length === 0 && !isApplicationsLoading && !isApplicationsError);

    return (
        <React.Fragment>
            <Row className="pb-3">
                <Col md={props.firstCol} className="pt-2"><label htmlFor="arbor-establishment-url">Establishment URL</label></Col>
                <Col md={props.secondCol}>
                    <input id="arbor-establishment-url" className={`form-control mt-1 ms-0 w-100 ${(errors.EstablishmentUrl) ? 'is-invalid' : ''}`} type="text" value={ArborConfig?.ApiCredentials?.EstablishmentUrl ?? ''} aria-invalid={Boolean(errors.EstablishmentUrl)} aria-describedby={errors.EstablishmentUrl ? 'arbor-establishment-url-error' : undefined} required
                        onChange={e => handleApiCredentialsChange('EstablishmentUrl', e.target.value)} />
                    {errors['EstablishmentUrl'] && <div id="arbor-establishment-url-error" className="invalid-feedback d-block" role="alert">{errors['EstablishmentUrl']}</div>}
                </Col>
            </Row>
            <Row className="pb-3">
                <Col md={props.firstCol} className="pt-2"><label htmlFor="arbor-provider-application-select">Arbor Application</label></Col>
                <Col md={props.secondCol}>
                    <select id="arbor-provider-application-select" data-testid="arbor-provider-application-select" className={`form-select mt-1 ms-0 w-100 ${errors.ArborApplication ? 'is-invalid' : ''}`} disabled={isApplicationsLoading || isApplicationsError} value={props.arborApplicationID ?? ''} aria-required="true" aria-invalid={Boolean(errors.ArborApplication)} aria-describedby={hasApplicationFeedback ? 'arbor-application-feedback' : undefined} onChange={(event) => props.setArborApplicationId(event.target.value ? event.target.value as GUID : null)}>
                        <option value="">{isApplicationsLoading ? 'Loading Arbor applications' : 'Select an Arbor application'}</option>
                        {applications.map((application) => <option key={application.applicationID} value={application.applicationID}>{application.applicationName}</option>)}
                    </select>
                    {hasApplicationFeedback && <div id="arbor-application-feedback">
                        {applications.length === 0 && !isApplicationsLoading && !isApplicationsError && <div className="invalid-feedback d-block" role="status">No Arbor applications are available</div>}
                        {errors.ArborApplication && <div className="invalid-feedback d-block" role="alert">{errors.ArborApplication}</div>}
                    </div>}
                </Col>
            </Row>
            <Row className="pb-3">
                <Col md={props.firstCol} className="pt-2"><label htmlFor="arbor-graphql-page-size">GraphQL Page Size</label></Col>
                <Col md={props.secondCol}>
                    <input id="arbor-graphql-page-size" className={`form-control mt-1 ms-0 w-100 ${(errors.GraphQLPageSize) ? 'is-invalid' : ''}`} type="number" min={1} value={ArborConfig?.GraphQLPageSize ?? ''} aria-invalid={Boolean(errors.GraphQLPageSize)} aria-describedby={errors.GraphQLPageSize ? 'arbor-graphql-page-size-error' : undefined} required
                        onChange={e => handleChange('GraphQLPageSize', parseInt(e.target.value))} />
                    {errors.GraphQLPageSize && <div id="arbor-graphql-page-size-error" className="invalid-feedback" role="alert" style={{ display: 'block', color: '#DC3245' }}>{errors.GraphQLPageSize}</div>}
                </Col>
            </Row>
            <Row className="pb-3">
                <Col md={props.firstCol} className="pt-2"><label htmlFor="arbor-graphql-photo-page-size">GraphQL Photo Page Size</label></Col>
                <Col md={props.secondCol}>
                    <input id="arbor-graphql-photo-page-size" className={`form-control mt-1 ms-0 w-100 ${(errors.GraphQLPhotoPageSize) ? 'is-invalid' : ''}`} type="number" min={1} value={ArborConfig?.GraphQLPhotoPageSize ?? ''} aria-invalid={Boolean(errors.GraphQLPhotoPageSize)} aria-describedby={errors.GraphQLPhotoPageSize ? 'arbor-graphql-photo-page-size-error' : undefined} required
                        onChange={e => handleChange('GraphQLPhotoPageSize', parseInt(e.target.value))} />
                    {errors.GraphQLPhotoPageSize && <div id="arbor-graphql-photo-page-size-error" className="invalid-feedback" role="alert" style={{ display: 'block', color: '#DC3245' }}>{errors.GraphQLPhotoPageSize}</div>}
                </Col>
            </Row>
            <Row className="pb-3">
                <Col md={props.firstCol} className="pt-2"><label htmlFor="arbor-retrieve-account-ids">Retrieve Account IDs?</label></Col>
                <Col md={props.secondCol}>
                    <select id="arbor-retrieve-account-ids" className="form-select mt-1 ms-0 w-100" value={ArborConfig?.RetrieveAccountIDs ? 'true' : 'false'}
                        onChange={e => handleChange('RetrieveAccountIDs', e.target.value === 'true')}>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                </Col>
            </Row>
            <Row className="pb-3">
                <Col md={props.firstCol} className="pt-2"><label htmlFor="arbor-retrieve-transitional-fsm">Retrieve transitional FSMs?</label></Col>
                <Col md={props.secondCol}>
                    <select id="arbor-retrieve-transitional-fsm" className="form-select mt-1 ms-0 w-100" value={ArborConfig?.RetrieveTransitionalFSM ? 'true' : 'false'}
                        onChange={e => handleChange('RetrieveTransitionalFSM', e.target.value === 'true')}>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                </Col>
            </Row>
            <input type="hidden" value={JSON.stringify(ArborConfig?.BiometricConsents) ?? ''} />
            <BiometricFilterEdit biometricsFilter={ArborConfig?.BiometricConsents} setBiometricsFilter={handleBiometricFilterChange} />
        </React.Fragment>
    );
});

export default ArborProviderConfigEdit;